import React, { useEffect, useState, useRef } from 'react';
import { Smile } from "lucide-react";
import SmileyModal from './SmileyModal';
import { smileySocket } from '../../engine/socket';
import { getFullAvatarUrl } from '../../services/api';
import useUserAvatar from '../../hooks/useUserAvatar';

const Player = ({
    i,
    chips,
    tableState,
    winData,
    sb,
    bb,
    dealer,
    avatars,
    playerRefs,
    tableRef,
    getSrcCard,
    rever,
    foldedPlayers,
    shouldShareCards,
    sharingCards,
    allInArr,
    gameOver,
    isRevealFinished,
    hideStack,
    frozenStack,
    tableId,
}) => {
    const [smileysOpen, setSmileysOpen] = useState(false);
    const [smiley, setSmiley] = useState(null);
    const [playerSmileys, setPlayerSmileys] = useState([])

    const sendSmiley = (smiley) => {
        const seat = tableState.seat;
        smileySocket.emit('send-smiley', {
            tableId, seat, smiley
        });
    }
    const onReceiveSmiley = (seat, smiley) => {
        setPlayerSmileys(prev => {
            return [...prev, { seat, smiley }];
        });
        setTimeout(() => {
            setPlayerSmileys(prev => {
                prev = prev.filter(s => s.seat !== seat);
                return prev;
            });
        }, 5000);
    }

    smileySocket.emit('join', tableId);
    const onConnect = () => {
    }

    useEffect(() => {
        
        smileySocket.on('connect', onConnect);
        smileySocket.on('receive-smiley', onReceiveSmiley);

        return () => {
            smileySocket.off('connect', onConnect);
            smileySocket.off('receive-smiley', onReceiveSmiley);
        }
    }, []);

    const [displayStack, setDisplayStack] = useState(chips?.stack ?? 0);
    const [isStackHidden, setIsStackHidden] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const isWaitingForShowdown = frozenStack !== undefined &&
        !gameOver && !(winData?.winStates?.length > 0);
    
    // Références pour verrouiller le solde
    const lastSafeStack = useRef(chips?.stack ?? 0);
    const isLocked = useRef(false);

    useEffect(() => {
        if (!gameOver) {
            setShowResult(false);
            setIsStackHidden(false);
            isLocked.current = false;
        }
    }, [gameOver]);

    useEffect(() => {
        if (chips?.stack === undefined) return;

        const actualStack = chips.stack;
        const isWinPhase = gameOver || (winData?.winStates?.length > 0);

        // The table state received at an all-in can already contain the final
        // stack.  Keep the snapshot from before the all-in visible/hidden until
        // the board reveal has completed.
        if (frozenStack !== undefined && !isWinPhase) {
            lastSafeStack.current = frozenStack;
            setDisplayStack(frozenStack);
            // The all-in has been announced, but showdown has not started yet.
            // Keep the previous balance visible; do not show "Suspense" early.
            setIsStackHidden(false);
            return;
        }

        if (frozenStack !== undefined && !isRevealFinished) {
            lastSafeStack.current = frozenStack;
            setDisplayStack(frozenStack);
            setIsStackHidden(true);
            return;
        }

        // ✅ CAS PRIORITAIRE : RECAVE ou PREMIER JOIN
        // Si le joueur était à 0 et reçoit un stack positif → recave ou première installation
        // On affiche immédiatement peu importe gameOver ou winData (sinon la logique showdown cache le stack 5s)
        if (!isWinPhase && lastSafeStack.current === 0 && actualStack > 0) {
            lastSafeStack.current = actualStack;
            setDisplayStack(actualStack);
            setIsStackHidden(false);
            isLocked.current = false;
            setShowResult(false);
            return;
        }

        // DÉTECTION PROACTIVE : Si le solde du serveur est supérieur à notre dernier solde sûr,
        // c'est une victoire certaine (ou un gain de pot).
        const isIncreasing = actualStack > lastSafeStack.current;

        // CAS 1 : Phase de jeu normale (Mises, Suivis, Blinds)
        // On n'autorise la mise à jour que si le solde baisse ou reste identique.
        if (!isWinPhase && !isIncreasing) {
            isLocked.current = false;
            lastSafeStack.current = actualStack;
            setDisplayStack(actualStack);
            setIsStackHidden(false);
            setShowResult(false);
            return;
        }

        // CAS 2 : ANTICIPATION (Augmentation détectée avant même le gameOver)
        // On bloque l'affichage sur la valeur d'avant pour préserver la surprise.
        if (!isWinPhase && isIncreasing) {
            isLocked.current = true;
            // On ne touche pas à displayStack, il reste sur lastSafeStack.current
            return;
        }

        // CAS 3 : Phase de victoire confirmée (gameOver ou winData présent)
        const winner = winData?.winStates?.find(w => w.seat === i);
        const isWinner = winner?.isWinner;
        const isLoser = winner && !winner.isWinner && !foldedPlayers.current.has(i);

        if (isWinner || isLoser) {
            isLocked.current = true;
            const hasShowdown = winData?.allCards && Object.values(winData.allCards).some(hand => hand && hand.length > 0);

            // SI PAS DE SHOWDOWN (All Fold)
            if (!hasShowdown) {
                const timer = setTimeout(() => {
                    setDisplayStack(actualStack);
                    lastSafeStack.current = actualStack;
                    isLocked.current = false;
                }, 1000);
                // Ensure showResult is true for "All Fold"
                setShowResult(true);
                return () => clearTimeout(timer);
            }

            // SI SHOWDOWN (Suspense complet)
            if (!isRevealFinished) {
                // Toujours bloqué sur l'ancien montant pendant l'ouverture des cartes
                setDisplayStack(lastSafeStack.current);
                setIsStackHidden(true); // Cacher le stack dès qu'on sait qu'il y a un showdown
            } else {
                // Séquence de suspense (les cartes sont ouvertes)
                // Only trigger the result display if it hasn't been shown yet
                if (!showResult) {
                    setIsStackHidden(true);

                    const timerResult = setTimeout(() => {
                        setShowResult(true); // Set showResult to true to display the badge
                    }, 1200);

                    const timerStack = setTimeout(() => {
                        setDisplayStack(actualStack); // Libération finale du nouveau solde
                        setIsStackHidden(false);
                        setShowResult(false); // Reset showResult after the full animation
                        lastSafeStack.current = actualStack;
                        isLocked.current = false;
                    }, 5000); // 5 secondes de suspense total

                    return () => {
                        clearTimeout(timerResult);
                        clearTimeout(timerStack);
                    };
                }
            }
        } else {
            // Perdants foldés ou spectateurs (pas de victoire ni de défaite affichée directement)
            if (!isWinPhase) {
                setDisplayStack(actualStack);
                lastSafeStack.current = actualStack;
                setShowResult(false);
                setIsStackHidden(false);
            }
        }
        }, [chips?.stack, frozenStack, gameOver, isRevealFinished, winData, i]);

    const { avatarUrl: fetchedAvatarUrl } = useUserAvatar(tableState.playerIds[i]);

    const [posCoords, setPosCoords] = useState({ tx: 0, ty: 0, px: 0, py: 0, zoom: 1 });

    useEffect(() => {
        if (shouldShareCards && playerRefs[i]?.current && tableRef.current) {
            const playerRect = playerRefs[i].current.getBoundingClientRect();
            const tableRect = tableRef.current.getBoundingClientRect();
            
            let currentZoom = 1;
            if (window.innerWidth <= 451 && window.innerWidth > 399) currentZoom = 0.9;
            if (window.innerWidth <= 399 && window.innerWidth > 350) currentZoom = 0.8;
            if (window.innerWidth <= 350) currentZoom = 0.7;

            // Animation de départ : Centre de la table
            const tableCenterX = tableRect.left + tableRect.width / 2;
            const tableCenterY = tableRect.top + tableRect.height / 2;

            setPosCoords({
                tx: tableCenterX,
                ty: tableCenterY,
                px: playerRect.left + playerRect.width / 2,
                py: playerRect.top + playerRect.height / 2,
                zoom: currentZoom
            });
        }
    }, [shouldShareCards, i, playerRefs, tableRef]);

    if (!tableState.playerNames[i]) return null;

    const avatarJson = avatars?.find(avt => avt.userId === tableState.playerIds[i]);
    let avatar = avatarJson?.avatar;

    // Fallback pour le joueur local : utiliser l'avatar du sessionStorage s'il est plus récent/différent
    const currentUserId = sessionStorage.getItem('userId');
    const userIdAvatar = `avatar_${currentUserId}`;
    if (tableState.playerIds[i] === currentUserId) {
        const sessionAvatar = sessionStorage.getItem(userIdAvatar);        
        if (sessionAvatar) {
            avatar = sessionAvatar;
        }
    }

    // Priorité : 
    // 1. Avatar récupéré du serveur via le hook
    // 2. Avatar du tableState ou sessionStorage formaté
    const avatarSrc = fetchedAvatarUrl || getFullAvatarUrl(avatar);

    const getCardCount = () => {
        if (tableState.playerCards && tableState.playerCards.length > 0) return tableState.playerCards.length;
        if (winData?.allCards) {
            const firstHand = winData.allCards.find(hand => hand && hand.length > 0);
            if (firstHand) return firstHand.length;
        }
        return 2; // Default to 2 if unknown
    };

    const winner = winData?.winStates?.find(w => w.seat === i);
    const isLoser = winner && !winner.isWinner && !foldedPlayers.current.has(i);
    const isWinner = winner?.isWinner;

    const cardCount = getCardCount();

    return (
        <>
            <div
                ref={playerRefs[i]}
                className={`
                    player 
                    seat${i} 
                    ${(winData?.winStates ?? []).length > 0 && winData.winStates.find(w => w.seat === i)?.isWinner && isRevealFinished ?'win': '' }
                    ${isWinner && isRevealFinished ? 'thunder-animation' : ''}
                    ${tableState.toAct === i ?'active': '' }`
                }
                style={{ borderRadius: 12 }}
                key={i}
            >
                <style>{`
                    @keyframes thunder {
                        0%, 100% { filter: brightness(1) saturate(1); transform: scale(1); box-shadow: 0 0 0px rgba(255,255,255,0); }
                        10%, 30%, 50%, 70%, 90% { filter: brightness(3) contrast(2) saturate(2); transform: scale(1.1); box-shadow: 0 0 30px rgba(255, 255, 255, 0.8); }
                        20%, 40%, 60%, 80% { filter: brightness(1) saturate(1); transform: scale(1); box-shadow: 0 0 0px rgba(255,255,255,0); }
                    }
                    .thunder-animation {
                        animation: thunder 5.8s ease-in-out infinite;
                        z-index: 20;
                    }
                `}</style>

                <div
                    style={{
                        position: 'absolute',
                        bottom: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: -1,
                    }}
                >
                    <div
                        style={{
                            width: '40pt',
                            height: '40pt',
                            backgroundColor: '#579',
                            borderRadius: '100%',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            border: isWinner && isRevealFinished && showResult ? '3px solid #FFD700' : '2px solid #EEE',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: isWinner && isRevealFinished && showResult
                                ? '0 0 15px #FFD700, 0 0 25px rgba(255, 215, 0, 0.8)'
                                : (tableState.toAct === i ? '0 0 0 0 rgba(255, 255, 0, 0.7)' : 'none'),
                            animation: tableState.toAct === i ? 'sonar-yellow 2s infinite' : 'none',
                        }}
                    >
                        <style>{`
                            @keyframes sonar-yellow {
                                0% { box-shadow: 0 0 0 0 rgba(255, 255, 0, 0.9); }
                                50% { box-shadow: 0 0 0 15px rgba(255, 255, 0, 0.5); }
                                100% { box-shadow: 0 0 0 25px rgba(255, 255, 0, 0); }
                            }
                        `}</style>
                        <img
                            src={avatarSrc || '/avatars/0.png'}
                            alt="avatar"
                            style={{
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%',
                            }}
                        />
                    </div>
                </div>
                <div className={`player-cards ${cardCount > 2 ? 'omaha' : ''}`}>
                    {(winData?.allCards ?? []).length > 0 ? (
                        <div className={`card-containers ${cardCount > 2 ? 'omaha' : ''}`} style={{ marginTop: '1.2em' }}>
                            {(winData.allCards[i] ?? []).length > 0 && !foldedPlayers.current.has(i) && (
                                <>
                                    {(winData.allCards[i]).map((card, idx) => (
                                        <div className={`card showdown-card-reveal ${isWinner && showResult ? 'winner-card-highlight' : ''}`} key={idx}>
                                            <img src={getSrcCard(card)} alt="" />
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {tableState.activeSeats.includes(i) && (
                                i === tableState.seat && tableState.playerCards != null ? (
                                    <div className={`card-containers ${cardCount > 2 ? 'omaha' : ''}`}
                                        style={{
                                          
                                            zIndex: -1,
                                        }}
                                    >
                                        {tableState.playerCards.map((card, idx) => (
                                            <div className="card" key={idx}>
                                                <img src={getSrcCard(card)} alt="" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div
                                        className={`card-containers ${cardCount > 2 ? 'omaha' : ''}`}
                                        style={{
                                           
                                            zIndex: -1,
                                        }}
                                    >
                                        {[...Array(cardCount)].map((_, idx) => (
                                            <div className="card" key={idx}><img src={rever} alt="" /></div>
                                        ))}
                                    </div>
                                )
                            )}

                            {shouldShareCards && (
                                <div
                                    className={`card-containers ${cardCount > 2 ? 'omaha' : ''}`}
                                    style={{

                                        zIndex: -1,
                                    }}
                                >
                                    {[...Array(cardCount)].map((_, idx, arr) => {
                                        const count = arr.length;
                                        // Spread cards slightly at the dealer position
                                        const xOffset = (idx - (count - 1) / 2) * -100;
                                        
                                        // Animation Coordinates
                                        const dx = sharingCards ? 0 : (posCoords.tx - posCoords.px) / posCoords.zoom;
                                        const dy = sharingCards ? 0 : (posCoords.ty - posCoords.py) / posCoords.zoom;
                                        const finalX = sharingCards ? 0 : dx + (xOffset / 100) * 30; // Scale xOffset for better centering

                                        // Sequential delay: one card after another for each player
                                        const delay = (idx * 0.2) + (i * 0.08);

                                        return (
                                            <div
                                                key={idx}
                                                className="card"
                                                style={{
                                                    transition: 'all 0.8s ease-out',
                                                    transitionDelay: `${delay}s`,
                                                    transform: `translate3d(${finalX}px, ${dy}px, 0) scale(${sharingCards ? 1 : 0.2}) rotate(${sharingCards ? 0 : 180}deg)`,
                                                    opacity: shouldShareCards ? 1 : 0,
                                                }}
                                            >
                                                <img src={rever} alt="" />
                                            </div>                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
 <div className="player-action-status">
                    {
                        (() => {
                            // Priorité au statut Fold persistant via le set foldedPlayers
                            if (foldedPlayers.current.has(i)) {
                                return <div className="action-badge badge-fold">Fold</div>;
                            }
                            
                            // Sinon, affichage des actions en cours (ex: raise, call)
                            const playerAction = tableState.actions?.find(item => item.playerId === i);
                            if (playerAction && playerAction.action !== 'fold') {
                                const action = playerAction.action;
                                const isAllIn = playerAction.amount > 0 && tableState.playerIds[i] && chips?.stack === 0; // Simplified all-in check
                                
                                let badgeClass = "badge-call";
                                let label = action;

                                if (action === 'raise' || action === 'bet') {
                                    badgeClass = "badge-raise";
                                    label = "Raise";
                                } else if (action === 'call') {
                                    badgeClass = "badge-call";
                                    label = "Call";
                                } else if (action === 'check') {
                                    badgeClass = "badge-call";
                                    label = "Check";
                                }

                                return (
                                    <div className={`action-badge ${badgeClass}`} key={i}>
                                        {label}
                                    </div>
                                );
                            } else if (isWinner && isRevealFinished && showResult) {
                                return (
                                    <div className="action-badge badge-win winner-badge-reveal" key={i}>
                                        <img src="/animate/thunder.svg" alt="Thunder" style={{ width: "3.5rem", height: "1.2rem", objectFit: "contain" }} />
                                    </div>
                                );
                            }
                            return null;
                        })()
                    }
                </div>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: -4,
                        bottom: 0,
                        right: -4,
                        backgroundColor: winData?.winStates && winData?.winStates[i].isWinner ? '#000' : '#000',
                        border: winData?.winStates && winData?.winStates[i].isWinner ? '2px solid #000000' : '2px solid #000000',
                        borderRadius: 12,
                        overflow: 'hidden',
                        zIndex: -1,
                        boxShadow: tableState.toAct === i ? '0px 0px 12px 2px #00FF99' : 'none',
                    }}
                >
                </div>

                <div className="player-name" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minWidth: '120px' }}>
                    <div>
                        {(tableState.playerNames?.[i] ?? '').length > 10
                            ? tableState.playerNames[i].slice(0, 10) + '...'
                            : (tableState.playerNames?.[i] || '')}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {dealer === i ? (
                            <span style={{ 
                                backgroundColor: 'white', 
                                color: 'black', 
                                borderRadius: '50%', 
                                width: '16px', 
                                height: '16px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                fontSize: '0.55rem', 
                                fontWeight: 'bold',
                                border: '1px solid #ccc'
                            }}>D</span>
                        ) : (
                            <>
                                {sb === i && (
                                    <span style={{ 
                                        backgroundColor: 'blue', 
                                        color: 'white', 
                                        borderRadius: '50%', 
                                        width: '16px', 
                                        height: '16px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        fontSize: '0.55rem', 
                                        fontWeight: 'bold'
                                    }}>SB</span>
                                )}
                            </>
                        )}
                        {bb === i && (
                            <span style={{ 
                                backgroundColor: 'red', 
                                color: 'white', 
                                borderRadius: '50%', 
                                width: '16px', 
                                height: '16px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                fontSize: '0.55rem', 
                                fontWeight: 'bold'
                            }}>BB</span>
                        )}
                    </div>
                </div>
                
               
                <div
                    style={{
                        height: 2,
                        width: '75%',
                        backgroundColor: '#00FF99',
                        marginTop: 2,
                        marginBottom: 2,
                        borderRadius: 2,
                        boxShadow: tableState.toAct === i ? '0px 0px 12px 4px #00FF99' : 'none',
                    }}
                ></div>
                <div className={`amount p_${i}`}>
                    {
                        (() => {
                            const playerAction = tableState.actions.find(item => item.playerId === i);
                            if (playerAction) {
                                return (
                                    <>
                                        <div key={i}
                                            style={{
                                                color: 'white',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {playerAction.action === 'check' || playerAction.action === 'fold'
                                                ? ''
                                                : `${playerAction.amount}`
                                            }
                                        </div>
                                        {playerAction.amount > 0 && (
                                            <div className="jeton">
                                                <img src={require("../../styles/image/jeton.png")} alt="" />
                                            </div>
                                        )}
                                    </>
                                );
                            }
                        })()
                    }
                </div>
                <div className="stacks">
                    {isWaitingForShowdown ? null : isStackHidden ? (
                        showResult ? (
                            winData?.winStates?.find(w => w.seat === i)?.isWinner ? (
                                <div className="hand-name-result winner-badge-reveal" style={{ color: '#00e5ff', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                    🏆 {winData.winStates.find(w => w.seat === i).handName || 'Gagnant'}
                                </div>
                            ) : (
                                <div className="hand-name-result lose-badge loser-badge-reveal" style={{ 
                                    backgroundColor: '#888888', 
                                    color: 'white', 
                                    fontSize: '0.7rem', 
                                    fontWeight: 'bold',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    textTransform: 'uppercase'
                                }}>
                                    {foldedPlayers.current.has(i) ? 'Fold' : 'Lose'}
                                </div>
                            )
                        ) : (
                            <div className="suspense-badge">
                                ⌛ Suspense...
                            </div>
                        )
                    ) : (
                        <>
                            {chips != null ? `${displayStack}` :
                            <div className="no-chips" style={{ opacity: 0.7 }}>0</div>}
                        </>
                    )}
                </div>

                {/* Barre de progression du temps (Countdown) */}
                {tableState.toAct === i && (
                    <div className="turn-countdown-container">
                        <div className="turn-countdown-bar"></div>
                    </div>
                )}
                
                
                <div
                    style={{
                        zIndex: 9999,
                        position: 'absolute',
                        top: '100%',
                        // right: i <= 3 ? '-25%' : 'auto',
                        // left: i > 3 ? '-25%' : 'auto',
                        color: 'white',
                        cursor: 'pointer',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        // paddingTop: 4,
                        // paddingBottom: 4,
                    }}
                    onClick={() => tableState.seat === i && setSmileysOpen(!smileysOpen)}
                >
                    {tableState.seat === i ? (
                        <>
                            {smiley ? (
                                <div>
                                    <img src={smiley} alt="Smiley" style={{ width: '100%', borderRadius: '4pt' }} />
                                </div>
                            ) : (
                                <div>
                                    {/* <img src={"/smileys/Tired 3D Sticker by Emoji.gif"} alt="Smiley" style={{ width: '100%', borderRadius: '4pt' }} /> */}
                                    <Smile size={32} fill='#ff9100ff' />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {playerSmileys.find(s => s.seat === i) && (
                                <div>
                                    <img src={playerSmileys.find(s => s.seat === i).smiley} alt="Smiley" style={{ width: '100%', borderRadius: '4pt' }} />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            
            {tableState.seat === i && (
                <SmileyModal 
                    isOpen={smileysOpen} 
                    onClose={() => setSmileysOpen(false)} 
                    onSelect={(smiley) => {
                        setSmiley(smiley);
                        setSmileysOpen(false);
                        sendSmiley(smiley);
                        setTimeout(() => {
                            setSmiley(null);
                        }, 8000);
                    }}
                />
            )}
        </>
    );
};
export default Player;
